/**
 * Forgot Password Page
 *
 * Allows users to request a password reset email.
 * Uses Supabase's built-in resetPasswordForEmail functionality.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        throw error;
      }

      setIsSuccess(true);
    } catch (err: any) {
      setError(err.message || t('auth.failedSendReset'));
    } finally {
      setIsLoading(false);
    }
  };

  // Success state
  if (isSuccess) {
    return (
      <div className="space-y-xl">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-success/10 mb-lg">
            <CheckCircle className="w-8 h-8 text-success" />
          </div>
          <h2 className="text-2xl font-bold text-primary mb-sm">{t('auth.checkEmail')}</h2>
          <p className="text-secondary text-sm">
            {t('auth.resetLinkSent', { email })}
          </p>
        </div>

        <div className="bg-blue/5 border border-blue/20 rounded-lg p-md">
          <p className="text-sm text-primary">
            {t('auth.checkSpam')}
          </p>
        </div>

        <div className="space-y-md">
          <button
            onClick={() => {
              setIsSuccess(false);
              setEmail('');
            }}
            className="btn btn-secondary w-full"
          >
            {t('auth.sendDifferentEmail')}
          </button>
          <button
            onClick={() => navigate('/login')}
            className="btn btn-primary w-full"
          >
            {t('auth.backToSignIn')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-xl">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-primary mb-sm">{t('auth.forgotPasswordTitle')}</h2>
        <p className="text-secondary text-sm">
          {t('auth.forgotPasswordDesc')}
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-md flex items-start gap-md">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-red-800 font-medium">{t('common.error')}</p>
            <p className="text-sm text-red-600 mt-xs">{error}</p>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-lg">
        <div>
          <label htmlFor="email" className="block text-sm font-medium mb-sm text-primary">
            {t('auth.emailAddress')}
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary" />
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('auth.enterEmail')}
              className="input pl-11"
              required
              autoFocus
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="btn btn-primary w-full"
        >
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              {t('auth.sending')}
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              {t('auth.sendResetLink')}
            </>
          )}
        </button>
      </form>

      {/* Back to Login */}
      <div className="pt-lg border-t border-border">
        <button
          type="button"
          onClick={() => navigate('/login')}
          className="flex items-center justify-center gap-sm text-sm text-secondary hover:text-primary transition-colors w-full"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('auth.backToSignIn')}
        </button>
      </div>
    </div>
  );
}
